import { xVerifyService } from '_services';
import { FIELDS_KEYS } from '_constants/forms';

import {
  getFileExtension,
  getFileExtensionsString,
  checkIfFileAllowed,
} from '_helpers/file';

export const validateEmail = (options) => async (value) => {
  let validationResult = true;
  const { setValidating, useEmailVerificationService } = options || {};

  if (useEmailVerificationService === false) {
    return validationResult;
  }

  if (setValidating) {
    setValidating(true);
  }

  let emailValue = value;

  if (emailValue.indexOf('+') > -1 && emailValue.indexOf('gmail.com') > -1) {
    emailValue =
      emailValue.substring(0, emailValue.indexOf('+')) +
      emailValue.substring(emailValue.indexOf('@'), emailValue.length);
  }

  const { status, reason } = await xVerifyService.verifyEmail(emailValue);

  if (xVerifyService.INVALID_EMAIL_STATUSES.includes(status)) {
    const defaultMessage = 'Email address is not valid';
    // eslint-disable-next-line max-len, prettier/prettier
    validationResult = xVerifyService.VALIDATION_MESSAGE[reason] || defaultMessage;
  }

  if (setValidating) {
    setValidating(false);
  }

  return validationResult;
};

export const validateYear = () => (value) => {
  const currentDate = new Date();

  if (currentDate.getFullYear() - value >= 25) {
    return 'The year cannot be more than 1 year in the future or more than 25 years in the past.';
  }

  return true;
};

export const validateAddress = () => (value) => {
  if (
    !/(?!.*(?:[pP][oO] [bB][oO][xX]))^[ a-zA-Z0-9\\/-]+$/.test(value) ||
    !/(?!.*(?:[pP][oO][bB][oO][xX]))^[ a-zA-Z0-9\\/-]+$/.test(value) ||
    !/(?!.*(?:[pP][oO][sS][tT] [oO][fF][fF][iI][cC][eE] [bB][oO][xX]))^[ a-zA-Z0-9\\/-]+$/.test(
      value
    ) ||
    /\bP(ost|ostal)?([ \.]*(O|0)(ffice)?)?([ \.]*Box)\b/i.test(value)
  ) {
    return 'PO Boxes are not acceptable';
  }

  return true;
};

export const validateCardNumber = (options) => (value) => {
  const { erroredFields } = options || {};
  const isTestCard = value === '0000000000000000';

  return !isTestCard && erroredFields.cardNumber
    ? erroredFields.cardNumber
    : true;
};

export const validateCVC = (options) => () => {
  const { erroredFields } = options || {};

  return erroredFields.cvc || true;
};

export const validateCardMonth = (options) => (value) => {
  const { values, clearErrors, checkIfCardExpired } = options || {};

  const isCardExpired = checkIfCardExpired({
    cardMonth: value,
    cardYear: values[FIELDS_KEYS.cardYear],
  });

  if (!isCardExpired) {
    clearErrors && clearErrors(FIELDS_KEYS.cardYear);
  }

  return isCardExpired && values[FIELDS_KEYS.cardYear]
    ? 'This card expired'
    : true;
};

export const validateCardYear = (options) => (value) => {
  const { values, clearErrors, checkIfCardExpired } = options || {};

  const isCardExpired = checkIfCardExpired({
    cardMonth: values[FIELDS_KEYS.cardMonth],
    cardYear: value,
  });

  if (!isCardExpired) {
    clearErrors && clearErrors(FIELDS_KEYS.cardMonth);
  }

  return isCardExpired && values[FIELDS_KEYS.cardMonth]
    ? 'This card expired'
    : true;
};

export const validateCategories = (options) => (value) => {
  const { values, fieldName } = options || {};

  const categories = values[fieldName]?.map((item) => {
    return item[FIELDS_KEYS.goodsCategory];
  });

  if (categories.filter((category) => category === value).length > 1) {
    return 'Duplication of categories is not allowed';
  }

  return true;
};

export const validatePhone = () => (value) => {
  const pureNumber = value.replace(/\D/g, '');

  if (pureNumber.length !== 10) {
    return 'Phone number should be equal 10 symbols';
  }

  return true;
};

export const validateFileSize = () => (value) => {
  const fileSizeMb = value.size / 1024 / 1024;

  if (fileSizeMb > 5) {
    return 'File size exceeds 5 Mb';
  }

  return true;
};

export const validateLogoFile = (options) => (value) => {
  const { fileTypes, noValidate } = options || {};

  if (noValidate) {
    return true;
  }

  const { allowedExtensions } =
    fileTypes?.find(({ code }) => code === 'TRADEMARK_LOGO') || {};

  const fileExtension = getFileExtension(value?.name);

  const isExtensionAllowed = checkIfFileAllowed(
    fileExtension,
    allowedExtensions
  );

  if (!isExtensionAllowed) {
    return `Incorrect file type. Allowed ${getFileExtensionsString(
      allowedExtensions
    )}`;
  }

  const fileSizeMb = value.size / 1024 / 1024;

  if (fileSizeMb > 5) {
    return 'File size exceeds 5 Mb';
  }

  return true;
};

export const validateSpecimenFile = (options) => (value) => {
  const { fileTypes, noValidate } = options || {};

  if (noValidate) {
    return true;
  }

  const { allowedExtensions } =
    fileTypes?.find(({ code }) => code === 'TRADEMARK_SPECIMEN') || {};

  const fileExtension = getFileExtension(value?.name);

  const isExtensionAllowed = checkIfFileAllowed(
    fileExtension,
    allowedExtensions
  );

  if (!isExtensionAllowed) {
    return `Incorrect file type. Allowed ${getFileExtensionsString(
      allowedExtensions
    )}`;
  }

  const fileSizeMb = value.size / 1024 / 1024;

  if (fileSizeMb > 5) {
    return 'File size exceeds 5 Mb';
  }

  return true;
};

export const validateDescription = (options) => (value) => {
  const { defaultMessage } = options || {};

  const cleanValue = value
    ?.replace(/[\r\n]+/gm, ' ')
    .replace(/\s{2,}/g, ' ')
    .trim();

  if (!cleanValue) {
    return defaultMessage || 'Please provide Description';
  }

  return true;
};
